import { GridColDef } from "@mui/x-data-grid";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import UpdateDriverPopup from "../../../components/Fleet/UpdateDriverPopup/UpdateDriverPopup";
import ConfirmActionButton from "../../../components/Fleet/ConfirmActionButton/ConfirmActionButton";
import { Box, Button, MenuItem, Select } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const clickableCellStyle = {
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
};

const formatDateValue = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    : "";

export const buildDriverTableColumns = (
  showTerminationDate: boolean,
): GridColDef[] => {
  const baseColumns: GridColDef[] = [
    { field: "firstName", headerName: "First Name", width: 200 },
    { field: "lastName", headerName: "Last Name", width: 200 },
    { field: "phoneNumber", headerName: "Phone number", width: 200 },
    {
      field: "birthDate",
      headerName: "Birth Date",
      width: 180,
      valueFormatter: (value: string) => formatDateValue(value),
    },
    {
      field: "workStartDate",
      headerName: "Work Start Date",
      width: 200,
      valueFormatter: (value: string) => formatDateValue(value),
    },
    { field: "driverCode", headerName: "Code", width: 200 },
    {
      field: "driverType",
      headerName: "Type",
      width: 200,
      valueFormatter: (value: string) => {
        return value ? camelCaseToSentenceCase(value) : value;
      },
    },
    { field: "company", headerName: "Company", width: 200 },
    {
      field: "payTo",
      headerName: "Pay To",
      width: 200,
      renderCell: (params) => <Box sx={clickableCellStyle}>{params.value}</Box>,
    },
    {
      field: "payType",
      headerName: "Pay Type",
      width: 200,
      valueFormatter: (value: string) => {
        return value ? camelCaseToSentenceCase(value) : value;
      },
    },
    {
      field: "usedEld",
      headerName: "Used ELD",
      width: 200,
      editable: true,
      renderEditCell: (params) => (
        <Select
          fullWidth
          value={(params.value as string) ?? ""}
          onChange={(event) => {
            const newValue = event.target.value;
            params.api.setEditCellValue(
              { id: params.id, field: params.field, value: newValue },
              event,
            );
          }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          <MenuItem value="samsara">Samsara</MenuItem>
          <MenuItem value="split">Split</MenuItem>
          <MenuItem value="other">Other</MenuItem>
          <MenuItem value="outside">Outside</MenuItem>
          <MenuItem value="notActive">Not Active</MenuItem>
        </Select>
      ),
      valueFormatter: (value: string) => {
        return value ? camelCaseToSentenceCase(value) : value;
      },
    },
    {
      field: "assignedTruckUnitNumber",
      headerName: "Assigned Truck",
      width: 200,
    },
    {
      field: "assignedTrailerUnitNumber",
      headerName: "Assigned Trailer",
      width: 200,
    },
    {
      field: "assignedDispatcher",
      headerName: "Assigned Dispatcher",
      width: 200,
    },
  ];

  if (showTerminationDate) {
    baseColumns.push({
      field: "terminationDate",
      headerName: "Termination Date",
      width: 200,
      valueFormatter: (value: string) => formatDateValue(value),
    });
  }

  baseColumns.push(
    {
      field: "details",
      headerName: "",
      width: 130,
      renderCell: (params) => (
        <Button
          component={RouterLink}
          to={`/fleet/drivers/${params.row.id}`}
          variant="outlined"
          size="small"
          onClick={(event) => event.stopPropagation()}
        >
          Details
        </Button>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 200,
      renderCell: (params) => <UpdateDriverPopup driver={params.row} />,
    },
    {
      field: "delete",
      headerName: "",
      width: 130,
      renderCell: (params) =>
        params.row.isActive ? (
          <ConfirmActionButton
            id={params.row.id}
            entityType="Driver"
            actionType="delete"
            entityLabel="driver"
            queryKey="drivers"
            buttonLabel="Delete"
            buttonColor="error"
            requireTerminationDate
            terminationDateLabel="Termination Date"
          />
        ) : (
          <ConfirmActionButton
            id={params.row.id}
            entityType="Driver"
            actionType="restore"
            entityLabel="driver"
            queryKey="drivers"
            buttonLabel="Restore"
            buttonColor="success"
          />
        ),
    },
  );

  return baseColumns;
};
